import React, { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery, useTheme } from '@material-ui/core';

import { Grid, Paper, Typography, Card, CardContent } from '@material-ui/core';
import { DateRange } from 'materialui-daterange-picker';
import { DateRangePicker } from 'components/DateRangePicker';
import { todayEnd, todayStart } from 'components/DateRangePicker/DefaultRanges';
import { useStore } from 'hooks/useRootStore';
import { useQuery } from 'react-query';
import { DataGrid, GridColumns } from '@material-ui/data-grid';
import { useStyles } from './styles';
import { useAppPreloader } from 'hooks/useAppPreloader';
interface ApiReportDay {
  date: string;
  read_ops: number;
  write_ops: number;
  total_ops: number;
}

interface ApiReportData {
  start_date: string;
  end_date: string;
  read_ops: number;
  write_ops: number;
  total_ops: number;
  by_day: ApiReportDay[];
}

const formatReportDate = (date?: string) => {
  if (!date) {
    return '-';
  }

  return date.split('-').reverse().join('/');
};

const toReportDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const API: FC = observer(() => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: todayStart,
    endDate: todayEnd,
  });
  const startDate = toReportDateParam(dateRange.startDate as Date);
  const endDate = toReportDateParam(dateRange.endDate as Date);
  const {
    logsTableStore: { getapiReports, showAllDateRange },
    notificationStore: { openNotification },
  } = useStore();

  const getapiReportsReq = useQuery(['getapiReportsReq', startDate, endDate], () => getapiReports(startDate, endDate), {
    onError: (e: any) => {
      openNotification('error', `Can't Get API Reports`);
    },
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const apiReportResponse = getapiReportsReq.data?.data;
  const apiReport: ApiReportData | undefined = apiReportResponse?.by_day ? apiReportResponse : apiReportResponse?.data;

  const columns: GridColumns = [
    {
      field: 'date',
      headerName: 'DATE',
      width: isMobile ? 120 : 180,
      renderCell: (params) => <span>{formatReportDate(params.row.date)}</span>,
    },
    {
      field: 'read_ops',
      headerName: 'READ OPS',
      width: isMobile ? 100 : 150,
      type: 'number',
    },
    {
      field: 'write_ops',
      headerName: 'WRITE OPS',
      width: isMobile ? 110 : 150,
      type: 'number',
    },
    {
      field: 'total_ops',
      headerName: 'TOTAL OPS',
      width: isMobile ? 110 : 150,
      type: 'number',
    },
  ];

  const rows = (apiReport?.by_day || []).map((item) => ({
    id: item.date,
    ...item,
  }));

  useAppPreloader(getapiReportsReq.isFetching);
  return (
    <>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <div className={classes.flex}>
          <DateRangePicker
            initialRange={dateRange}
            customRanges={[
              { label: 'Show All', startDate: showAllDateRange.startDate, endDate: showAllDateRange.endDate },
            ]}
            onChange={setDateRange}
          />
        </div>
      </Grid>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Grid container spacing={2} className={classes.summaryGrid}>
                <Grid item xs={12} sm={4}>
                  <Card className={classes.statsCard}>
                    <CardContent>
                      <Typography className={classes.statsLabel}>Read Ops</Typography>
                      <Typography className={classes.statsValue}>
                        {(apiReport?.read_ops || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card className={classes.statsCard}>
                    <CardContent>
                      <Typography className={classes.statsLabel}>Write Ops</Typography>
                      <Typography className={classes.statsValue}>
                        {(apiReport?.write_ops || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card className={classes.statsCard}>
                    <CardContent>
                      <Typography className={classes.statsLabel}>Total Ops</Typography>
                      <Typography className={classes.statsValue}>
                        {(apiReport?.total_ops || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <div className={classes.reportRange}>
                <Typography variant='body2'>
                  Report period: {formatReportDate(apiReport?.start_date)} - {formatReportDate(apiReport?.end_date)}
                </Typography>
              </div>

              <div className={classes.dataGrid}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  pageSize={pageSize}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  rowCount={rows.length}
                  page={page}
                  onPageChange={(params) => setPage(params.page)}
                  onPageSizeChange={(params) => {
                    setPageSize(params.pageSize);
                    setPage(0);
                  }}
                  disableSelectionOnClick
                  density={isMobile ? 'compact' : 'standard'}
                />
              </div>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </>
  );
});

export default API;
